import {
  Alert,
  Box,
  Button,
  Collapse,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowRightIcon from 'mdi-react/ArrowRightIcon';
import CheckCircleIcon from 'mdi-react/CheckCircleIcon';
import ChevronDownIcon from 'mdi-react/ChevronDownIcon';
import ChevronRightIcon from 'mdi-react/ChevronRightIcon';
import ChartBarIcon from 'mdi-react/ChartBarIcon';
import CloseIcon from 'mdi-react/CloseIcon';
import CloudUploadOutlineIcon from 'mdi-react/CloudUploadOutlineIcon';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import InformationOutlineIcon from 'mdi-react/InformationOutlineIcon';
import LightbulbOutlineIcon from 'mdi-react/LightbulbOutlineIcon';
import MessageOutlineIcon from 'mdi-react/MessageOutlineIcon';
import PencilOutlineIcon from 'mdi-react/PencilOutlineIcon';
import RobotOutlineIcon from 'mdi-react/RobotOutlineIcon';
import TagOutlineIcon from 'mdi-react/TagOutlineIcon';
import ToolboxOutlineIcon from 'mdi-react/ToolboxOutlineIcon';
import { array, bool, func, object, string } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dropzone from 'react-dropzone';
import Agent from 'services/api/Agent';
import { I18n } from 'utils/i18n';
import toDropzoneAccept from 'utils/toDropzoneAccept';

const getStyles = theme => ({
  stepper: {
    mb: theme.spacing(3),
    '& .MuiStepLabel-label': { fontSize: theme.typography.pxToRem(13) },
  },
  dropzone: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: '12px',
    padding: theme.spacing(4, 2),
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: theme.palette.background.default,
  },
  conflictGroup: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    mb: theme.spacing(1.5),
    overflow: 'hidden',
  },
  conflictHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: theme.palette.background.default,
    cursor: 'pointer',
  },
  conflictBody: {
    padding: theme.spacing(0, 1.5, 1.5),
  },
  prefixChip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: theme.spacing(0.25, 1),
    borderRadius: '6px',
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 500,
  },
  heroCard: {
    position: 'relative',
    borderRadius: '14px',
    padding: theme.spacing(2.5),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}0d 0%, ${theme.palette.primary.main}03 100%)`,
    border: `1px solid ${theme.palette.primary.main}22`,
    marginBottom: theme.spacing(2),
    overflow: 'hidden',
  },
  heroTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
  },
  heroMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  heroAvatar: {
    width: 44,
    height: 44,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    flexShrink: 0,
  },
  heroNumber: {
    fontSize: theme.typography.pxToRem(28),
    fontWeight: 700,
    lineHeight: 1.1,
    color: theme.palette.text.primary,
  },
  typeChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  typeChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5, 1),
    borderRadius: '999px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    color: theme.palette.text.primary,
    '& svg': { color: theme.palette.text.secondary },
  },
  sectionTitle: {
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: theme.spacing(1),
  },
  segmentBar: {
    display: 'flex',
    height: 8,
    borderRadius: '999px',
    overflow: 'hidden',
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(1.5),
  },
  segmentOverride: { backgroundColor: theme.palette.warning.main },
  segmentRename: { backgroundColor: theme.palette.info.main },
  segmentSkip: { backgroundColor: theme.palette.text.disabled },
  segmentImport: { backgroundColor: theme.palette.success.main },
  statTiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  statTile: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '10px',
    padding: theme.spacing(1.25, 1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
    backgroundColor: theme.palette.background.paper,
  },
  statTileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  statTileValue: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 700,
    lineHeight: 1.1,
  },
  statTileDim: { opacity: 0.4 },
  statDotImport: {
    width: 8,
    height: 8,
    borderRadius: '999px',
    backgroundColor: theme.palette.success.main,
  },
  statDotOverride: {
    width: 8,
    height: 8,
    borderRadius: '999px',
    backgroundColor: theme.palette.warning.main,
  },
  statDotRename: {
    width: 8,
    height: 8,
    borderRadius: '999px',
    backgroundColor: theme.palette.info.main,
  },
  statDotSkip: {
    width: 8,
    height: 8,
    borderRadius: '999px',
    backgroundColor: theme.palette.text.disabled,
  },
  renamedCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    overflow: 'hidden',
  },
  renamedHeader: {
    padding: theme.spacing(1.25, 1.75),
    backgroundColor: theme.palette.background.default,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  renamedItem: {
    padding: theme.spacing(1.25, 1.75),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  renamedTypePill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0.75),
    borderRadius: '6px',
    backgroundColor: theme.palette.action.hover,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    flexShrink: 0,
  },
  renamedFrom: {
    color: theme.palette.text.secondary,
    textDecoration: 'line-through',
  },
  renamedTo: {
    fontWeight: 600,
  },
  conflictItem: {
    borderTop: `1px solid ${theme.palette.divider}`,
    paddingTop: theme.spacing(1.5),
    marginTop: theme.spacing(1.5),
  },
  bulkActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  smallButton: {
    padding: theme.spacing(0.5, 1),
    fontSize: theme.typography.pxToRem(12),
    minHeight: 0,
    fontWeight: 500,
  },
});

const TYPE_LABELS = {
  agent: 'Agents',
  skill: 'Skills',
  tool: 'Tools',
  instruction: 'Instructions',
  tag: 'Tags',
  metric: 'Metrics',
  chatFlow: 'Chat flows',
};

const TYPE_ICONS = {
  agent: RobotOutlineIcon,
  skill: LightbulbOutlineIcon,
  tool: ToolboxOutlineIcon,
  instruction: MessageOutlineIcon,
  tag: TagOutlineIcon,
  metric: ChartBarIcon,
  chatFlow: CogOutlineIcon,
};

const renderTypeIcon = (type, size = 14) => {
  const Icon = TYPE_ICONS[type] || CogOutlineIcon;
  return <Icon size={size} />;
};

const ConflictGroup = ({
  type,
  items,
  resolutions,
  renameNames,
  namePrefix,
  onChange,
  onRenameNameChange,
  onBulk,
  expanded,
  onToggle,
  styles,
  showConflictActions = true,
}) => (
  <Box sx={styles.conflictGroup}>
    <Box sx={styles.conflictHeader} onClick={() => onToggle(type)}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {expanded ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
          }}
        >
          {TYPE_LABELS[type] || type} ({items.length})
        </Typography>
      </Box>
      <Box sx={styles.bulkActions} onClick={e => e.stopPropagation()}>
        {showConflictActions && (
          <>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              sx={styles.smallButton}
              onClick={() => onBulk(type, 'override')}
            >
              {I18n.t('AgenticChatFlow.ImportOverrideAll')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              sx={styles.smallButton}
              onClick={() => onBulk(type, 'skip')}
            >
              {I18n.t('AgenticChatFlow.ImportSkipAll')}
            </Button>
          </>
        )}
      </Box>
    </Box>
    <Collapse in={expanded}>
      <Box sx={styles.conflictBody}>
        {items.map(item => {
          const resolution = resolutions[item.key] || item.resolution || 'import';
          const isRename = resolution === 'rename';
          const renameValue = renameNames[item.key] ?? '';
          const renameError = isRename && !renameValue.trim();
          const hasConflict = Boolean(item.message);
          return (
            <Box key={item.key} sx={styles.conflictItem}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {item.name}
                {namePrefix && item.importName && item.importName !== item.name && (
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      ml: 1,
                      fontWeight: 400,
                    }}
                  >
                    → {item.importName}
                  </Typography>
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: hasConflict ? 'text.secondary' : 'success.main',
                  display: 'block',
                  mb: 1,
                }}
              >
                {hasConflict
                  ? item.message
                  : I18n.t('AgenticChatFlow.ImportNoNameConflict', {
                      name: item.importName || item.name,
                    })}
              </Typography>
              <RadioGroup value={resolution} onChange={e => onChange(item.key, e.target.value)}>
                <FormControlLabel
                  value="override"
                  control={<Radio size="small" />}
                  label={I18n.t('AgenticChatFlow.ImportResolutionOverride')}
                />
                <FormControlLabel
                  value="skip"
                  control={<Radio size="small" />}
                  label={I18n.t('AgenticChatFlow.ImportResolutionSkip')}
                />
                <FormControlLabel
                  value="rename"
                  control={<Radio size="small" />}
                  label={I18n.t('AgenticChatFlow.ImportResolutionRename')}
                />
              </RadioGroup>
              {isRename && (
                <TextField
                  fullWidth
                  size="small"
                  required
                  label={I18n.t('AgenticChatFlow.ImportRenameName')}
                  placeholder={`${namePrefix || ''}${item.name}`}
                  value={renameValue}
                  onChange={e => onRenameNameChange(item.key, e.target.value)}
                  error={renameError}
                  helperText={
                    renameError
                      ? I18n.t('AgenticChatFlow.ImportRenameNameRequired')
                      : I18n.t('AgenticChatFlow.ImportRenameNameHelper')
                  }
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Collapse>
  </Box>
);

ConflictGroup.propTypes = {
  type: string.isRequired,
  items: array.isRequired,
  resolutions: object.isRequired,
  renameNames: object.isRequired,
  namePrefix: string.isRequired,
  onChange: func.isRequired,
  onRenameNameChange: func.isRequired,
  onBulk: func.isRequired,
  expanded: bool.isRequired,
  onToggle: func.isRequired,
  styles: object.isRequired,
  showConflictActions: bool,
};

const ImportAgentModal = ({ open, onClose, onImported = null }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [namePrefix, setNamePrefix] = useState('');
  const [preview, setPreview] = useState(null);
  const [resolutions, setResolutions] = useState({});
  const [renameNames, setRenameNames] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [confirmSummary, setConfirmSummary] = useState(null);
  const lastLoadedPrefixRef = useRef(null);

  const reset = () => {
    setStep(0);
    setFile(null);
    setNamePrefix('');
    setPreview(null);
    setPreviewReady(false);
    setResolutions({});
    setRenameNames({});
    setExpandedGroups({});
    setConfirmSummary(null);
    lastLoadedPrefixRef.current = null;
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const loadPreview = useCallback(
    async (nextPrefix, { isPrefixRefresh = false } = {}) => {
      if (!file) return;
      if (isPrefixRefresh) {
        setPreviewLoading(true);
      } else {
        setLoading(true);
      }
      try {
        const data = await Agent.importPreview(file, nextPrefix);
        setPreview(data.preview);
        setPreviewReady(true);
        setResolutions(data.preview.resolutions || {});
        setRenameNames({});
        const groups = data.preview.groupedConflicts || {};
        setExpandedGroups(Object.fromEntries(Object.keys(groups).map(k => [k, true])));
        lastLoadedPrefixRef.current = nextPrefix;
      } finally {
        if (isPrefixRefresh) {
          setPreviewLoading(false);
        } else {
          setLoading(false);
        }
      }
    },
    [file],
  );

  useEffect(() => {
    if (step !== 1 || !file || !previewReady) return undefined;
    if (lastLoadedPrefixRef.current === namePrefix) return undefined;

    const timer = setTimeout(() => {
      loadPreview(namePrefix, { isPrefixRefresh: true });
    }, 400);
    return () => clearTimeout(timer);
  }, [namePrefix, step, file, previewReady, loadPreview]);

  const handleNextFromUpload = async () => {
    await loadPreview(namePrefix);
    setStep(1);
  };

  const handleNextFromPreview = () => {
    const summary = {
      total: preview?.summary?.total || 0,
      override: Object.values(resolutions).filter(v => v === 'override').length,
      skip: Object.values(resolutions).filter(v => v === 'skip').length,
      rename: Object.values(resolutions).filter(v => v === 'rename').length,
    };
    setConfirmSummary(summary);
    setStep(2);
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const data = await Agent.importConfirm(file, { resolutions, namePrefix, renameNames });
      onImported?.(data?.result);
      handleClose();
    } finally {
      setLoading(false);
    }
  };

  const groupedConflicts = preview?.groupedConflicts || {};
  const groupedEntities = preview?.groupedEntities || groupedConflicts;
  const conflictCount = preview?.summary?.conflicts || 0;
  const showEntityGroups = Boolean(preview) && (conflictCount > 0 || previewLoading);

  const conflictingByType = useMemo(() => {
    const result = {};
    Object.entries(groupedEntities).forEach(([type, items]) => {
      const conflicting = items.filter(item => item.message);
      if (conflicting.length) {
        result[type] = conflicting;
      }
    });
    return result;
  }, [groupedEntities]);

  const cleanByType = useMemo(() => {
    const result = {};
    Object.entries(groupedEntities).forEach(([type, items]) => {
      const clean = items.filter(item => !item.message);
      if (clean.length) {
        result[type] = clean;
      }
    });
    return result;
  }, [groupedEntities]);

  const cleanImportCount = useMemo(
    () => Object.values(cleanByType).reduce((sum, items) => sum + items.length, 0),
    [cleanByType],
  );

  const entityTypeCounts = useMemo(() => {
    const fromPreview = preview?.entityTypeCounts;
    const source =
      fromPreview && Object.keys(fromPreview).length > 0
        ? fromPreview
        : Object.fromEntries(
            Object.entries(groupedEntities).map(([type, items]) => [type, items.length]),
          );
    return Object.entries(source)
      .map(([type, count]) => ({ type, count }))
      .filter(item => item.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [preview?.entityTypeCounts, groupedEntities]);

  const renamedConflicts = useMemo(
    () =>
      (preview?.conflicts || [])
        .filter(c => resolutions[c.key] === 'rename')
        .map(c => ({
          key: c.key,
          type: c.type,
          from: c.name,
          to: renameNames[c.key]?.trim() || '',
        })),
    [preview?.conflicts, resolutions, renameNames],
  );

  const summaryTotal = confirmSummary?.total || 0;
  const summaryImport = Math.max(
    summaryTotal -
      (confirmSummary?.override || 0) -
      (confirmSummary?.rename || 0) -
      (confirmSummary?.skip || 0),
    0,
  );
  const segmentPct = key => {
    if (!summaryTotal) return 0;
    return ((confirmSummary?.[key] || 0) / summaryTotal) * 100;
  };
  const importPct = summaryTotal ? (summaryImport / summaryTotal) * 100 : 0;

  const hasMissingRenameNames = useMemo(
    () =>
      (preview?.conflicts || []).some(
        c => resolutions[c.key] === 'rename' && !renameNames[c.key]?.trim(),
      ),
    [preview?.conflicts, resolutions, renameNames],
  );

  const stepLabels = useMemo(
    () => [
      {
        label: I18n.t('AgenticChatFlow.ImportStepUpload'),
        sub: I18n.t('AgenticChatFlow.ImportStepUploadSub'),
      },
      {
        label: I18n.t('AgenticChatFlow.ImportStepPreview'),
        sub: I18n.t('AgenticChatFlow.ImportStepPreviewSub'),
      },
      {
        label: I18n.t('AgenticChatFlow.ImportStepConfirm'),
        sub: I18n.t('AgenticChatFlow.ImportStepConfirmSub'),
      },
    ],
    [],
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {I18n.t('AgenticChatFlow.ImportAgent')}
        <IconButton size="small" onClick={handleClose} aria-label={I18n.t('Common.close')}>
          <CloseIcon size={18} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={styles.stepper} alternativeLabel>
          {stepLabels.map(s => (
            <Step key={s.label}>
              <StepLabel>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {s.label}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {s.sub}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box sx={{ mt: 2 }}>
            <Dropzone
              accept={toDropzoneAccept('application/zip,.zip')}
              maxSize={10 * 1024 * 1024}
              multiple={false}
              onDrop={accepted => setFile(accepted[0] || null)}
            >
              {({ getRootProps, getInputProps, isDragActive }) => (
                <Box
                  {...getRootProps()}
                  sx={[
                    styles.dropzone,
                    {
                      borderColor: isDragActive ? 'primary.main' : undefined,
                      backgroundColor: isDragActive ? 'action.hover' : undefined,
                    },
                  ]}
                >
                  <input {...getInputProps()} />
                  <CloudUploadOutlineIcon size={32} style={{ opacity: 0.5, marginBottom: 8 }} />
                  <Typography variant="body1">
                    {I18n.t('AgenticChatFlow.ImportDropzone')}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      display: 'block',
                      mt: 0.5,
                    }}
                  >
                    {I18n.t('AgenticChatFlow.ImportDropzoneHint')}
                  </Typography>
                  {file && (
                    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>
                      {file.name}
                    </Typography>
                  )}
                </Box>
              )}
            </Dropzone>
          </Box>
        )}

        {step === 1 && (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
              }}
            >
              {I18n.t('AgenticChatFlow.ImportPreviewTitle')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                mb: 0.75,
              }}
            >
              {I18n.t('AgenticChatFlow.ImportNamePrefix')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={I18n.t('AgenticChatFlow.ImportNamePrefixPlaceholder')}
              value={namePrefix}
              onChange={e => setNamePrefix(e.target.value)}
              sx={{ mb: 0.75 }}
            />
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mb: 2,
              }}
            >
              {I18n.t('AgenticChatFlow.ImportNamePrefixHelper')}
            </Typography>
            {previewLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <CircularProgress size={16} />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {I18n.t('AgenticChatFlow.ImportCheckingPrefixConflicts')}
                </Typography>
              </Box>
            )}
            {!previewLoading && conflictCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {I18n.t('AgenticChatFlow.ImportConflictsDetected', { count: conflictCount })}
                </Typography>
                <Typography variant="body2">
                  {I18n.t('AgenticChatFlow.ImportConflictsHelper')}
                </Typography>
              </Alert>
            )}
            {showEntityGroups && Object.keys(conflictingByType).length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  opacity: previewLoading ? 0.6 : 1,
                  mb: cleanImportCount > 0 ? 2 : 0,
                }}
              >
                {Object.entries(conflictingByType).map(([type, items]) => (
                  <ConflictGroup
                    key={type}
                    type={type}
                    items={items}
                    resolutions={resolutions}
                    renameNames={renameNames}
                    namePrefix={namePrefix}
                    onChange={(key, value) => setResolutions(prev => ({ ...prev, [key]: value }))}
                    onRenameNameChange={(key, value) =>
                      setRenameNames(prev => ({ ...prev, [key]: value }))
                    }
                    onBulk={(typeKey, value) => {
                      setResolutions(prev => {
                        const next = { ...prev };
                        (conflictingByType[typeKey] || []).forEach(item => {
                          next[item.key] = value;
                        });
                        return next;
                      });
                    }}
                    expanded={Boolean(expandedGroups[type])}
                    onToggle={typeKey =>
                      setExpandedGroups(prev => ({ ...prev, [typeKey]: !prev[typeKey] }))
                    }
                    styles={styles}
                    showConflictActions
                  />
                ))}
              </Box>
            )}
            {!previewLoading && cleanImportCount > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {I18n.t('AgenticChatFlow.ImportCleanImportsTitle', { count: cleanImportCount })}
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {I18n.t('AgenticChatFlow.ImportCleanImportsHelper')}
                </Typography>
                {Object.entries(cleanByType).map(([type, items]) => (
                  <Box key={type} sx={{ mb: 0.75 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                      }}
                    >
                      {TYPE_LABELS[type] || type}
                    </Typography>
                    <Typography variant="body2" component="div">
                      {items
                        .map(item => item.importName || item.name)
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </Box>
                ))}
              </Alert>
            )}
            {!previewLoading && !conflictCount && !cleanImportCount && (
              <Alert
                severity={namePrefix ? 'success' : 'info'}
                icon={namePrefix ? undefined : <InformationOutlineIcon size={18} />}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {I18n.t('AgenticChatFlow.ImportNoConflictsTitle')}
                </Typography>
                <Typography variant="body2">
                  {namePrefix
                    ? I18n.t('AgenticChatFlow.ImportNoConflictsWithPrefix', { prefix: namePrefix })
                    : I18n.t('AgenticChatFlow.ImportNoConflicts')}
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                mb: 2,
              }}
            >
              {I18n.t('AgenticChatFlow.ImportConfirmTitle')}
            </Typography>
            <Box sx={styles.heroCard}>
              <Box sx={styles.heroTopRow}>
                <Box sx={styles.heroMeta}>
                  <Box sx={styles.heroAvatar}>
                    <CheckCircleIcon size={22} />
                  </Box>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {I18n.t('AgenticChatFlow.ImportReadyTitle')}
                    </Typography>
                    <Typography sx={styles.heroNumber}>
                      {I18n.t('AgenticChatFlow.ImportHeroTotal', { count: summaryTotal })}
                    </Typography>
                  </Box>
                </Box>
                {namePrefix && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.5,
                      }}
                    >
                      {I18n.t('AgenticChatFlow.ImportConfirmNamePrefix')}
                    </Typography>
                    <Box component="span" sx={styles.prefixChip}>
                      {namePrefix}
                    </Box>
                  </Box>
                )}
              </Box>
              {entityTypeCounts.length > 0 && (
                <Box sx={styles.typeChips}>
                  {entityTypeCounts.map(({ type, count }) => (
                    <Box key={type} sx={styles.typeChip}>
                      {renderTypeIcon(type, 14)}
                      <span>{count}</span>
                      <span style={{ opacity: 0.65 }}>{TYPE_LABELS[type] || type}</span>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Typography sx={styles.sectionTitle}>
              {I18n.t('AgenticChatFlow.ImportResolutionSummary')}
            </Typography>
            {summaryTotal > 0 && (
              <Box sx={styles.segmentBar}>
                {summaryImport > 0 && (
                  <Box sx={[styles.segmentImport, { width: `${importPct}%` }]} />
                )}
                {(confirmSummary?.override || 0) > 0 && (
                  <Box sx={[styles.segmentOverride, { width: `${segmentPct('override')}%` }]} />
                )}
                {(confirmSummary?.rename || 0) > 0 && (
                  <Box sx={[styles.segmentRename, { width: `${segmentPct('rename')}%` }]} />
                )}
                {(confirmSummary?.skip || 0) > 0 && (
                  <Box sx={[styles.segmentSkip, { width: `${segmentPct('skip')}%` }]} />
                )}
              </Box>
            )}
            <Box sx={styles.statTiles}>
              <Box sx={[styles.statTile, summaryImport === 0 && styles.statTileDim]}>
                <Box sx={styles.statTileHeader}>
                  <Box sx={styles.statDotImport} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {I18n.t('AgenticChatFlow.ImportResolutionImportShort')}
                  </Typography>
                </Box>
                <Typography sx={styles.statTileValue} component="span">
                  {summaryImport}
                </Typography>
              </Box>
              <Box
                sx={[styles.statTile, (confirmSummary?.override || 0) === 0 && styles.statTileDim]}
              >
                <Box sx={styles.statTileHeader}>
                  <Box sx={styles.statDotOverride} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {I18n.t('AgenticChatFlow.ImportResolutionOverrideShort')}
                  </Typography>
                </Box>
                <Typography sx={styles.statTileValue} component="span">
                  {confirmSummary?.override || 0}
                </Typography>
              </Box>
              <Box
                sx={[styles.statTile, (confirmSummary?.rename || 0) === 0 && styles.statTileDim]}
              >
                <Box sx={styles.statTileHeader}>
                  <Box sx={styles.statDotRename} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {I18n.t('AgenticChatFlow.ImportResolutionRenameShort')}
                  </Typography>
                </Box>
                <Typography sx={styles.statTileValue} component="span">
                  {confirmSummary?.rename || 0}
                </Typography>
              </Box>
              <Box sx={[styles.statTile, (confirmSummary?.skip || 0) === 0 && styles.statTileDim]}>
                <Box sx={styles.statTileHeader}>
                  <Box sx={styles.statDotSkip} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    {I18n.t('AgenticChatFlow.ImportResolutionSkipShort')}
                  </Typography>
                </Box>
                <Typography sx={styles.statTileValue} component="span">
                  {confirmSummary?.skip || 0}
                </Typography>
              </Box>
            </Box>

            {renamedConflicts.length > 0 && (
              <Box sx={styles.renamedCard}>
                <Box sx={styles.renamedHeader}>
                  <PencilOutlineIcon size={16} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    {I18n.t('AgenticChatFlow.ImportRenamedEntities')}
                  </Typography>
                  <Box sx={{ ml: 'auto' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {renamedConflicts.length}
                    </Typography>
                  </Box>
                </Box>
                {renamedConflicts.map(item => (
                  <Box key={item.key} sx={styles.renamedItem}>
                    <Box sx={styles.renamedTypePill}>
                      {renderTypeIcon(item.type, 12)}
                      <span>{item.type}</span>
                    </Box>
                    <Typography variant="body2" sx={styles.renamedFrom}>
                      {item.from}
                    </Typography>
                    <ArrowRightIcon size={14} style={{ opacity: 0.5, flexShrink: 0 }} />
                    <Typography variant="body2" sx={styles.renamedTo} noWrap>
                      {item.to}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit" sx={{ textTransform: 'none' }}>
          {I18n.t('AgenticChatFlow.Cancel')}
        </Button>
        {step > 0 && (
          <Button onClick={() => setStep(s => s - 1)} sx={{ textTransform: 'none' }}>
            {I18n.t('AgenticChatFlow.Previous')}
          </Button>
        )}
        {step === 0 && (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={!file || loading}
            onClick={handleNextFromUpload}
            sx={{ textTransform: 'none' }}
          >
            {I18n.t('AgenticChatFlow.Next')}
          </Button>
        )}
        {step === 1 && (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={loading || previewLoading || hasMissingRenameNames}
            onClick={handleNextFromPreview}
            sx={{ textTransform: 'none' }}
          >
            {I18n.t('AgenticChatFlow.Next')}
          </Button>
        )}
        {step === 2 && (
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={loading}
            onClick={handleImport}
            sx={{ textTransform: 'none' }}
          >
            {I18n.t('AgenticChatFlow.Import')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ImportAgentModal.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  onImported: func,
};

export default ImportAgentModal;
